import React from 'react';
import { Box, IconButton, Slider, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { PlayArrow, Pause, RestartAlt, Loop, SkipNext, SkipPrevious } from '@mui/icons-material';
import { COLOR } from './tokens';

export const REPLAY_SPEEDS = [0.5, 1, 2, 4];

/**
 * Transport bar for replays: restart, step back/forward, play/pause,
 * loop toggle, speed (0.5/1/2/4x) and a scrub slider.
 */
export default function ReplayControls({
    playing,
    step,
    totalSteps,
    speed = 1,
    loop = false,
    onTogglePlay,
    onRestart,
    onStepBack,
    onStepForward,
    onSeekStart,
    onSeekChange,
    onSeekCommit,
    onSpeedChange,
    onLoopToggle,
}) {
    const iconSx = { color: COLOR.textMuted, '&:hover': { color: COLOR.text } };
    return (
        <Box sx={{ px: 1.5, py: 1, borderTop: `1px solid ${COLOR.border}` }} data-testid="replay-controls">
            <Slider
                size="small"
                value={step}
                min={0}
                max={totalSteps}
                step={1}
                onMouseDown={onSeekStart}
                onChange={onSeekChange}
                onChangeCommitted={onSeekCommit}
                aria-label="Replay seek"
                sx={{ mb: 0.5, color: COLOR.primary, '& .MuiSlider-thumb': { width: 12, height: 12 } }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                <IconButton size="small" onClick={onRestart} aria-label="Restart" sx={iconSx}>
                    <RestartAlt />
                </IconButton>
                <IconButton size="small" onClick={onStepBack} aria-label="Step back" disabled={step <= 0} sx={iconSx}>
                    <SkipPrevious />
                </IconButton>
                <Tooltip title={playing ? 'Pause' : 'Play'}>
                    <IconButton
                        onClick={onTogglePlay}
                        aria-label={playing ? 'Pause' : 'Play'}
                        sx={{
                            color: COLOR.primaryContrast,
                            bgcolor: COLOR.primary,
                            '&:hover': { bgcolor: COLOR.primaryHover },
                        }}
                    >
                        {playing ? <Pause /> : <PlayArrow />}
                    </IconButton>
                </Tooltip>
                <IconButton
                    size="small"
                    onClick={onStepForward}
                    aria-label="Step forward"
                    disabled={step >= totalSteps}
                    sx={iconSx}
                >
                    <SkipNext />
                </IconButton>
                {onLoopToggle ? (
                    <IconButton
                        size="small"
                        onClick={onLoopToggle}
                        data-testid="replay-loop-toggle"
                        aria-label={loop ? 'Loop on' : 'Loop off'}
                        aria-pressed={loop}
                        sx={{ color: loop ? COLOR.primary : COLOR.textMuted }}
                    >
                        <Loop />
                    </IconButton>
                ) : null}
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={speed}
                    onChange={(_, value) => { if (value != null && onSpeedChange) onSpeedChange(Number(value)); }}
                    aria-label="Playback speed"
                    sx={{ ml: 0.5 }}
                >
                    {REPLAY_SPEEDS.map((s) => (
                        <ToggleButton
                            key={s}
                            value={s}
                            aria-label={`${s}x speed`}
                            sx={{
                                px: 1,
                                py: 0.25,
                                fontSize: '0.75rem',
                                color: COLOR.textMuted,
                                borderColor: COLOR.border,
                                '&.Mui-selected, &.Mui-selected:hover': { color: COLOR.primaryContrast, bgcolor: COLOR.primary },
                            }}
                        >
                            {s}x
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
                <Typography variant="caption" sx={{ color: COLOR.textMuted, ml: 0.5, fontVariantNumeric: 'tabular-nums' }}>
                    Step {step} / {totalSteps}
                </Typography>
            </Box>
        </Box>
    );
}
